import { lazy } from 'react'
import { RouteObject } from 'react-router'
import { PATH_NAME } from '~/constants'
import { GuestLayoutOutlet, MasterLayoutOutlet, NotFoundLayoutOutlet } from '~/layouts'
import NotAuthorized from '~/pages/Error/NotAuthorized'
import NotFound from '~/pages/Error/NotFound'
import { DefaultRoute } from './DefaultRoute'
import { AuthGuardOutlet } from '~/guards/AuthGuard/AuthGuard'
import { FleetButtonRouteData } from '~/components/FleetButton/FleetButton.model'
import { RoleGuardOutlet } from '~/guards/RoleGuard/RoleGuard'
import { GuestGuardOutlet } from '~/guards/GuestGuard/GuestGuard'
import { IconTestPage } from '~/pages/TestPage/icontestpage.component'
import OidcGuardOutlet from '~/guards/OidcGuard/OidcGuard'

const lazyRetry = function <T>(componentImport: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem('retry-lazy-refreshed') ?? 'false')
    componentImport()
      .then((component: T) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false')
        resolve(component)
      })
      .catch((error: any) => {
        if (!hasRefreshed) {
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true')
          return window.location.reload()
        }
        reject(error)
      })
  })
}

const Login = lazy(() => lazyRetry(() => import('~/pages/Login')))
const RegisterSerialNumber = lazy(() => lazyRetry(() => import('~/pages/RegisterSerialNumber')))
const TermsOfService = lazy(() => lazyRetry(() => import('~/pages/TermsOfService')))
const UpgradePage = lazy(() => lazyRetry(() => import('~/pages/UpgradePage')))
const SearchResultPage = lazy(() => lazyRetry(() => import('~/pages/SearchResult')))
const Dashboard = lazy(() => lazyRetry(() => import('~/pages/Dashboard')))
const Groups = lazy(() => lazyRetry(() => import('~/pages/Groups')))
const GroupDetailPage = lazy(() => lazyRetry(() => import('~/pages/Groups/components/GroupDetail')))
const Devices = lazy(() => lazyRetry(() => import('~/pages/Devices')))
const DeviceDetailPage = lazy(() => lazyRetry(() => import('~/pages/Devices/DeviceDetail')))
const Assets = lazy(() => lazyRetry(() => import('~/pages/Assets')))
const SFEManagement = lazy(() => lazyRetry(() => import('~/pages/SFEManagement')))
const AssetDetails = lazy(() => lazyRetry(() => import('~/pages/Assets/AssetDetails')))
const Activity = lazy(() => lazyRetry(() => import('~/pages/Activity')))
const ServerError = lazy(() => lazyRetry(() => import('~/pages/Error/ServerError')))
const Admin = lazy(() => lazyRetry(() => import('~/pages/Admin')))
const Configurations = lazy(() => lazyRetry(() => import('~/pages/Configurations')))
const Alerts = lazy(() => lazyRetry(() => import('~/pages/Alerts')))
const ProfileSetting = lazy(() => lazyRetry(() => import('~/pages/ProfileSettings')))
const SSOCallBack = lazy(() => lazyRetry(() => import('~/pages/SSOCallBack')))

const BaseStationGroups = lazy(() => lazyRetry(() => import('~/pages/BaseStation/BaseStationGroups')))
const BaseStationGroupDetail = lazy(() => lazyRetry(() => import('~/pages/BaseStation/BaseStationGroupDetail/BaseStationGroupDetail')))
const BaseStationHardware = lazy(() => lazyRetry(() => import('~/pages/BaseStation/BaseStationHardware/BaseStationHardware')))
const BaseStationDetail = lazy(() => lazyRetry(() => import('~/pages/BaseStation/BaseStationDetail')))
const BaseStationFirmwarePage = lazy(() => lazyRetry(() => import('~/pages/BaseStation/BaseStationFirmware/FirmwarePageContainer')))

const CertificateList = lazy(() => lazyRetry(() => import('~/pages/Certificates/CertificatesList')))
const CertificateDetail = lazy(() => lazyRetry(() => import('~/pages/Certificates/CertificateDetail')))

const routesWithDisabledFleetSelector: RouteObject[] = [
  {
    path: PATH_NAME.DASHBOARD.path,
    element: <Dashboard />,
    handle: {
      requireRoles: PATH_NAME.DASHBOARD.requiredRoles
    }
  },
  {
    path: PATH_NAME.USERS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.USERS.requiredRoles
    }
  },
  {
    path: PATH_NAME.UPDATE_USER.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.UPDATE_USER.requiredRoles
    }
  },
  {
    path: PATH_NAME.CREATE_USER.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.CREATE_USER.requiredRoles
    }
  },
  {
    path: PATH_NAME.OTAP_AGENTS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.OTAP_AGENTS.requiredRoles
    }
  },
  {
    path: PATH_NAME.OTAP_AGENT_DETAILS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.OTAP_AGENTS.requiredRoles
    }
  },
  {
    path: PATH_NAME.SECURITY_KEYS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.SECURITY_KEYS.requiredRoles
    }
  },
  {
    path: PATH_NAME.SECURITY_KEY_DETAILS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.SECURITY_KEYS.requiredRoles
    }
  },
  {
    path: PATH_NAME.ENABLEFLEET_LICENSES.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.ENABLEFLEET_LICENSES.requiredRoles
    }
  },
  {
    path: PATH_NAME.LOG_FILES.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.LOG_FILES.requiredRoles
    }
  },
  {
    path: PATH_NAME.LOG_FILES_CATEGORY.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.LOG_FILES_CATEGORY.requiredRoles
    }
  },
  {
    path: PATH_NAME.LOG_FILES_WINDOW_USER.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.LOG_FILES_WINDOW_USER.requiredRoles
    }
  },
  {
    path: PATH_NAME.SETTINGS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.SETTINGS.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_JOBS_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_JOBS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_REGISTRATION_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_REGISTRATION_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_AGENT_QUEUE_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_AGENT_QUEUE_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.path,
    element: <Activity />,
    handle: {
      requireRoles: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.FIRMWARES_PAGE.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.FIRMWARES_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.FIRMWARES_DETAIL.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.FIRMWARES_DETAIL.requiredRoles
    }
  },
  {
    path: PATH_NAME.DEVICE_APPLICATIONS.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.DEVICE_APPLICATIONS.requiredRoles
    }
  },
  {
    path: PATH_NAME.DEVICE_APPLICATION_DETAILS.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.DEVICE_APPLICATION_DETAILS.requiredRoles
    }
  },
  {
    path: PATH_NAME.SUPPORTING_APPLICATIONS.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.SUPPORTING_APPLICATIONS.requiredRoles
    }
  },
  {
    path: PATH_NAME.SUPPORTING_APPLICATION_DETAILS.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.SUPPORTING_APPLICATION_DETAILS.requiredRoles
    }
  },
  {
    path: PATH_NAME.CUSTOMIZATION_PROFILES.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.CUSTOMIZATION_PROFILES.requiredRoles
    }
  },
  {
    path: PATH_NAME.DEVICES_DETAIL_PAGE.path,
    element: <DeviceDetailPage />,
    handle: {
      requireRoles: PATH_NAME.DEVICES_DETAIL_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.ASSET_DETAILS_PAGE.path,
    element: <AssetDetails />,
    handle: {
      requireRoles: PATH_NAME.ASSETS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.GROUPS_PAGE_DETAIL.path,
    element: <GroupDetailPage />,
    handle: {
      requireRoles: PATH_NAME.GROUPS_PAGE_DETAIL.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_DETAILS.path,
    element: <BaseStationDetail />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_DETAILS.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_GROUP_DETAILS.path,
    element: <BaseStationGroupDetail />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_GROUP_DETAILS.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_FIRMWARES_PAGE.path,
    element: <BaseStationFirmwarePage />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_FIRMWARES_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_FIRMWARES_DETAIL.path,
    element: <BaseStationFirmwarePage />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_FIRMWARES_DETAIL.requiredRoles
    }
  },
  {
    path: PATH_NAME.CERTIFICATES_PAGE_PATH.path,
    element: <CertificateList />,
    handle: {
      requireRoles: PATH_NAME.CERTIFICATES_PAGE_PATH.requiredRoles
    }
  },
  {
    path: PATH_NAME.CERTIFICATE_DETAIL.path,
    element: <CertificateDetail />,
    handle: {
      requireRoles: PATH_NAME.CERTIFICATE_DETAIL.requiredRoles
    }
  }
]

const routes: RouteObject[] = [
  {
    path: PATH_NAME.SEARCH_GLOBAL.path,
    element: <SearchResultPage />,
    handle: {
      requireRoles: PATH_NAME.SEARCH_GLOBAL.requiredRoles
    }
  },
  {
    path: '/icon-usage',
    element: <IconTestPage />
  },
  {
    path: PATH_NAME.ALERTS_PAGE.path,
    element: <Alerts />,
    handle: {
      requireRoles: PATH_NAME.ALERTS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.PROFILE_SETTING.path,
    element: <ProfileSetting />,
    handle: {
      requireRoles: PATH_NAME.PROFILE_SETTING.requiredRoles
    }
  },
  {
    path: PATH_NAME.GROUPS_PAGE.path,
    element: <Groups />,
    handle: {
      requireRoles: PATH_NAME.GROUPS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.DEVICES.path,
    element: <Devices />,
    handle: {
      requireRoles: PATH_NAME.DEVICES.requiredRoles
    }
  },
  {
    path: PATH_NAME.ASSETS_PAGE.path,
    element: <Assets />,
    handle: {
      requireRoles: PATH_NAME.ASSETS_PAGE.requiredRoles
    }
  },
  {
    path: PATH_NAME.SFE_MANAGEMENT.path,
    element: <SFEManagement />,
    handle: {
      requireRoles: PATH_NAME.SFE_MANAGEMENT.requiredRoles
    }
  },
  {
    path: PATH_NAME.FLEETS.path,
    element: <Admin />,
    handle: {
      requireRoles: PATH_NAME.FLEETS.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_GROUPS_PAGE_PATH.path,
    element: <BaseStationGroups />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_GROUPS_PAGE_PATH.requiredRoles
    }
  },
  {
    path: PATH_NAME.BASESTATION_HARDWARE_PAGE_PATH.path,
    element: <BaseStationHardware />,
    handle: {
      requireRoles: PATH_NAME.BASESTATION_DETAILS.requiredRoles
    }
  },
  {
    path: PATH_NAME.FACTORYSETTINGS.path,
    element: <Configurations />,
    handle: {
      requireRoles: PATH_NAME.FACTORYSETTINGS.requiredRoles
    }
  }
]

function fleetSelectorDisableLoader(): FleetButtonRouteData {
  return { fleetSelectorDisabled: true }
}

export const allRoutes: RouteObject[] = [
  {
    element: <AuthGuardOutlet />,
    children: [
      {
        element: <MasterLayoutOutlet />,
        children: [
          {
            path: '/',
            element: <DefaultRoute />
          },
          {
            element: <RoleGuardOutlet />,
            children: [...routes]
          }
        ]
      },
      {
        element: <MasterLayoutOutlet />,
        children: [
          {
            element: <RoleGuardOutlet />,
            children: [...routesWithDisabledFleetSelector]
          },

        ],
        loader: fleetSelectorDisableLoader
      },
      {
        element: <MasterLayoutOutlet />,
        children: [
          {
            element: <NotAuthorized />,
            path: PATH_NAME.NOT_AUTHORIZED.path
          }
        ],
        loader: fleetSelectorDisableLoader
      }
    ]
  },
  {
    element: <GuestLayoutOutlet />,
    children: [
      {
        element: <GuestGuardOutlet />,
        children: [
          {
            path: PATH_NAME.LOGIN_PAGE.path,
            element: <Login />
          }
        ]
      },
      {
        path: PATH_NAME.REGISTER_SERIAL_NUMBER_PAGE.path,
        element: <RegisterSerialNumber />
      },
      {
        path: PATH_NAME.UPGRADE_PAGE.path,
        element: <UpgradePage />
      },
      {
        path: '/sso-login',
        element: <SSOCallBack />
      },
      {
        path: PATH_NAME.TERMS_OF_SERVICE_PAGE.path,
        element: <TermsOfService />
      },
      {
        element: <OidcGuardOutlet />,
        children: [
          {
            path: PATH_NAME.PROVIDER_LOGIN_PAGE.path,
            element: <Login />
          }
        ]
      }
    ]
  },
  {
    element: <NotFoundLayoutOutlet />,
    children: [
      { path: PATH_NAME.SERVER_ERROR.path, element: <ServerError /> },
      { path: '/*', element: <NotFound /> }
    ]
  }
]
